import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, setDoc, increment, updateDoc } from 'firebase/firestore';
import { getAuth, setPersistence, signInAnonymously, browserSessionPersistence } from 'firebase/auth';
import { db } from './firebaseConfig';
import './App.css';
import GameUI from './GameUI';
import StatsPage from './StatsPage';
import Trivia from './Trivia';
import ChallengePage from './ChallengePage';
import Leaderboard from './Leaderboard';
import { format } from 'date-fns';

function App() {
	const [gameData, setGameData] = useState(null);
	const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
	const [highestIndex, setHighestIndex] = useState(0);
	const [userGuess, setUserGuess] = useState('');
	// Instead of separate arrays for attempts and seriesMatches, we combine them:
	// Each attempt will be an object: { guess: string, isSeriesMatch: boolean }
	const [attempts, setAttempts] = useState([]);
	const [gameOver, setGameOver] = useState(false);
	const [success, setSuccess] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [searchResults, setSearchResults] = useState([]); // Store search results
	const [emailCopied, setEmailCopied] = useState(false);
	const [copied, setCopied] = useState(false);
	const [shareButtonText, setShareButtonText] = useState('Share Results');
	const [shareMessage, setShareMessage] = useState('');
	const [allMovieTitles, setAllMovieTitles] = useState([]);

	const defaultStats = {
		gamesPlayed: 0,
		gamesWon: 0,
		currentStreak: 0,
		maxStreak: 0,
		guessDistribution: Array(6).fill(0), // Initialize 6 slots for guess distribution
	};

	async function ensureAuth() {
		const auth = getAuth();
		try {
			await setPersistence(auth, browserSessionPersistence);
			if (!auth.currentUser) {
				await signInAnonymously(auth);
			}
		} catch (error) {
			console.error('Error during authentication:', error);
		}
	}

	useEffect(() => {
		ensureAuth();
	}, []);

	useEffect(() => {
		// If the user has played today, set gameOver to true,
		// and read the last shareMessage from localStorage.
		const today = format(new Date(), 'yyyy-MM-dd');
		const lastPlayedDate = localStorage.getItem('lastPlayedDate');
		if (lastPlayedDate === today) {
			setGameOver(true);
			setSuccess(JSON.parse(localStorage.getItem('lastGameSuccess')) || false);
			setHighestIndex(5);
			// Retrieve the share message so we don't lose it
			const savedMessage = localStorage.getItem('shareMessage');
			if (savedMessage) {
				setShareMessage(JSON.parse(savedMessage));
			}
		}
	}, []);

	useEffect(() => {
		const fetchDailyMovie = async () => {
			try {
				const today = format(new Date(), 'yyyy-MM-dd');

				// Query the "daily_movies" collection for today's date
				const dailyMoviesRef = collection(db, 'daily_movies');
				const dailyMovieQuery = query(dailyMoviesRef, where('date', '==', today));
				const dailyMovieSnapshot = await getDocs(dailyMovieQuery);

				if (dailyMovieSnapshot.empty) {
					console.error(`No daily movie found for ${today}.`);
					return;
				}

				// We assume only one "daily movie" doc per date
				const dailyMovieDoc = dailyMovieSnapshot.docs[0];
				const { movieId } = dailyMovieDoc.data();

				// Query the "movies" collection for that movieId
				const moviesRef = collection(db, 'movies');
				const movieQuery = query(moviesRef, where('movieId', '==', movieId));
				const movieSnapshot = await getDocs(movieQuery);

				if (movieSnapshot.empty) {
					console.error(`No movie found with movieId "${movieId}".`);
					return;
				}

				// We assume only one doc per movieId
				const movieDoc = movieSnapshot.docs[0];
				const movieData = movieDoc.data();
				setGameData(movieData);
			} catch (error) {
				console.error('Error fetching daily movie:', error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchDailyMovie();
	}, []);

	//Fetch all titles once, store in allMovieTitles
	useEffect(() => {
		const fetchAllTitles = async () => {
			try {
				const movieTitlesRef = doc(db, 'movie_titles', 'movie_titles_all');
				const docSnap = await getDoc(movieTitlesRef);
				if (docSnap.exists()) {
					setAllMovieTitles(docSnap.data().titles);
				} else {
					console.error('No movie titles document found');
				}
			} catch (err) {
				console.error('Error fetching all movie titles:', err);
			}
		};

		fetchAllTitles();
	}, []);

	async function recordGameResult(isWin, attempts) {
		const auth = getAuth();
		const today = format(new Date(), 'yyyy-MM-dd');
		const dailyStatsRef = doc(db, 'daily_stats', today);

		try {
			// Ensure user is authenticated before writing
			if (!auth.currentUser) {
				await signInAnonymously(auth);
			}

			// Check if document exists
			const docSnap = await getDoc(dailyStatsRef);
			if (!docSnap.exists()) {
				// Initialize the document safely if it does not exist
				await setDoc(dailyStatsRef, {
					totalGamesPlayed: 0,
					totalGamesWon: 0,
					guessDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 },
				});
			}

			// Increment stats
			const updates = {
				totalGamesPlayed: increment(1),
			};

			if (isWin) {
				updates.totalGamesWon = increment(1);
				if (attempts >= 1 && attempts <= 6) {
					updates[`guessDistribution.${attempts}`] = increment(1);
				}
			}

			// Only one update to avoid double increments
			await updateDoc(dailyStatsRef, updates);
		} catch (error) {
			console.error('Error updating daily stats:', error);
		}
	}

	/**
	 * Handle a guess or skip from the *currently viewed* block.
	 * - If the guess is correct, game ends.
	 * - If the guess is incorrect or skipped, reveal (or jump to) the next block:
	 *      highestIndex + 1
	 * - If we run out of blocks, game ends.
	 */
	const handleGuess = () => {
		if (gameOver) return;

		const attemptText = userGuess.trim() === '' ? 'Skipped' : userGuess;
		// Determine if the guess is a series match
		const isSeriesMatch = gameData.series && gameData.series.includes(attemptText);
		// Create a single attempt object with both guess and series-match flag
		const newAttempt = { guess: attemptText, isSeriesMatch };
		const newAttempts = [...attempts, newAttempt];

		setAttempts(newAttempts);

		const isCorrectGuess = attemptText.toLowerCase() === gameData.title?.toLowerCase();

		if (isCorrectGuess) {
			setSuccess(true);
			setGameOver(true);
			setHighestIndex(gameData.quotes.length - 1);
			handleGameOver(newAttempts, true);
			return;
		}

		const nextIndex = highestIndex + 1;
		if (nextIndex >= gameData.quotes.length) {
			setGameOver(true);
			setHighestIndex(gameData.quotes.length - 1);
			handleGameOver(newAttempts, false);
			return;
		}

		setHighestIndex(nextIndex);
		setCurrentQuoteIndex(nextIndex);
		setUserGuess('');
	};

	const handleGameOver = async (attemptsArray, isWin) => {
		const savedStats = JSON.parse(localStorage.getItem('gameStats')) || defaultStats;

		const newStats = {
			gamesPlayed: savedStats.gamesPlayed + 1,
			gamesWon: isWin ? savedStats.gamesWon + 1 : savedStats.gamesWon,
			currentStreak: isWin ? savedStats.currentStreak + 1 : 0, // Reset streak if not a win
			maxStreak: isWin ? Math.max(savedStats.currentStreak + 1, savedStats.maxStreak) : savedStats.maxStreak,
			guessDistribution: [...savedStats.guessDistribution], // Clone the existing array
		};

		const guessCount = attemptsArray.length;
		if (isWin && guessCount >= 1 && guessCount <= 6) {
			newStats.guessDistribution[guessCount - 1] += 1;
		}

		try {
			await recordGameResult(isWin, guessCount);
		} catch (error) {
			console.error('Failed to record game result:', error);
		}

		const today = format(new Date(), 'yyyy-MM-dd');
		const shareMessage = generateShareMessage(attemptsArray);

		localStorage.setItem('gameStats', JSON.stringify(newStats));
		localStorage.setItem('lastPlayedDate', today);
		localStorage.setItem('lastGameSuccess', isWin);
		localStorage.setItem('shareMessage', JSON.stringify(shareMessage));
		setShareMessage(shareMessage);
	};

	const generateShareMessage = (attemptsArray) => {
		if (!gameData) return '';

		const totalQuotes = gameData.quotes.length;
		const resultIcons = Array(totalQuotes).fill('⬜');

		for (let i = 0; i < attemptsArray.length; i++) {
			const { guess, isSeriesMatch } = attemptsArray[i];
			const actual = gameData.title?.toLowerCase();

			if (guess.toLowerCase() === actual) {
				resultIcons[i] = '🟩';
				break;
			} else if (isSeriesMatch) {
				resultIcons[i] = '🟦';
			} else {
				resultIcons[i] = '🟥';
			}
		}

		const todayStr = format(new Date(), 'yyyy-MM-dd');
		return `Movie Quotes Guesser - ${todayStr}\n 📜 ${resultIcons.join(' ')}\nhttps://moviequotesguesser.com`;
	};

	/**
	 * Autocomplete / Search handling, search using in-memory list
	 */
	const handleInputChange = (event) => {
		const input = event.target.value;
		setUserGuess(input);

		if (input.length > 0) {
			// Use the allMovieTitles array from state
			const filteredResults = allMovieTitles
				.filter((title) => title.toLowerCase().includes(input.toLowerCase()))
				.sort((a, b) => {
					const aStartsWith = a.toLowerCase().startsWith(input.toLowerCase());
					const bStartsWith = b.toLowerCase().startsWith(input.toLowerCase());
					if (aStartsWith && !bStartsWith) return -1;
					if (!aStartsWith && bStartsWith) return 1;
					return 0;
				});

			setSearchResults(filteredResults);
		} else {
			setSearchResults([]);
		}
	};

	const handleEmailCopy = () => {
		navigator.clipboard.writeText('contact@moviequotesguesser.com');
		setEmailCopied(true);
		setTimeout(() => setEmailCopied(false), 2000);
	};

	const handleTwitterShare = () => {
		const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
			"🎥 Try out Movie Quotes Guesser! Can you guess today's movie? 🕵️‍♂️"
		)}&url=${encodeURIComponent('https://www.moviequotesguesser.com/')}&hashtags=${encodeURIComponent(
			'MovieQuotesGuesser,Movies'
		)}`;
		window.open(twitterShareUrl, '_blank', 'noopener,noreferrer');
	};

	const handleFacebookShare = () => {
		const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
			'https://www.moviequotesguesser.com/'
		)}`;
		window.open(facebookShareUrl, '_blank', 'noopener,noreferrer');
	};

	const handleCopyLink = () => {
		navigator.clipboard.writeText(window.location.href);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	/**
	 * Share results logic
	 */
	const handleShareResults = () => {
		// If shareMessage is empty, generate it on the fly (should be rare)
		const message = shareMessage || generateShareMessage(attempts);

		navigator.clipboard.writeText(message).then(() => {
			setShareButtonText('Copied!');
			setTimeout(() => setShareButtonText('Share Results'), 2000);
		});
	};
	/**
	 * Reset game
	 */
	const resetGame = () => {
		setCurrentQuoteIndex(0);
		setHighestIndex(0);
		setUserGuess('');
		setAttempts([]);
		setGameOver(false);
		setSuccess(false);
		setSearchResults([]);
	};

	// Show a loading state while data is being fetched
	if (isLoading) {
		return (
			<div className="loading-container">
				<div className="spinner"></div>
			</div>
		);
	}

	// If we finished loading but still have no gameData, show an error or fallback
	if (!gameData) {
		return (
			<div className="no-daily-movie-container">
				<div className="no-daily-movie-card">
					<h2 className="no-daily-movie-title">Hang Tight!</h2>
					<p className="no-daily-movie-text">
						We are working hard to make today's quotes. Please come back in an hour or so.
					</p>
				</div>
			</div>
		);
	}

	// Render the main game UI
	return (
		<Router>
			<Routes>
				{/* Default Route for the Game */}
				<Route
					path="/"
					element={
						<GameUI
							gameOver={gameOver}
							success={success}
							gameData={gameData}
							currentQuoteIndex={currentQuoteIndex}
							highestIndex={highestIndex}
							userGuess={userGuess}
							attempts={attempts} // attempts now include series match info
							searchResults={searchResults}
							handleInputChange={handleInputChange}
							handleGuess={handleGuess}
							resetGame={resetGame}
							setUserGuess={setUserGuess}
							setCurrentQuoteIndex={setCurrentQuoteIndex}
							handleShareResults={handleShareResults}
							handleEmailCopy={handleEmailCopy}
							emailCopied={emailCopied}
							handleTwitterShare={handleTwitterShare}
							handleFacebookShare={handleFacebookShare}
							handleCopyLink={handleCopyLink}
							copied={copied}
							shareButtonText={shareButtonText}
						/>
					}
				/>

				{/* Route for the Statistics Page */}
				<Route
					path="/stats"
					element={
						<StatsPage
							handleEmailCopy={handleEmailCopy}
							emailCopied={emailCopied}
							handleTwitterShare={handleTwitterShare}
							handleFacebookShare={handleFacebookShare}
							handleCopyLink={handleCopyLink}
							copied={copied}
							stats={JSON.parse(localStorage.getItem('gameStats')) || defaultStats}
						/>
					}
				/>
				<Route
					path="/trivia"
					element={
						<Trivia
							handleEmailCopy={handleEmailCopy}
							emailCopied={emailCopied}
							handleTwitterShare={handleTwitterShare}
							handleFacebookShare={handleFacebookShare}
							handleCopyLink={handleCopyLink}
							copied={copied}
						/>
					}
				/>
				<Route
					path="/challenge"
					element={<ChallengePage gameData={gameData} allMovieTitles={allMovieTitles} />}
				/>
				<Route path="/leaderboard/:challengeId" element={<Leaderboard />} />
			</Routes>
		</Router>
	);
}

export default App;
